import { useState, useRef, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  PencilIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  UserIcon,
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  TrashIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  CardHeader,
  Badge,
  getStatusBadgeVariant,
  ContentLoader,
  EntityLink,
  EmptyState,
  BackButton,
  InsurerLogo,
  SearchableSelect,
  Modal,
  Select,
  Input,
} from '@/components/common';
import { PolicyNumber, policyNumberLabel } from '@/components/common/PolicyNumber';
import { PolicyMembersCard } from '@/components/policies/PolicyMembersCard';
// Imported by path rather than through components/common/index.ts, which is
// outside this task's file domain.
import { RecordTimestamps } from '@/components/common/RecordTimestamps';
// The agent REASSIGNMENT picker is server-driven (its options are a search
// result from GET /brokers/posps/, not a static list), so it needs the
// variant that trusts `options` as-is. The Document Type picker above it is a
// genuinely local, static list and keeps common/SearchableSelect.
import { SearchableSelect as ServerSearchableSelect } from '@/components/ui/SearchableSelect';
import {
  usePolicy,
  usePolicyDocuments,
  usePolicyPayments,
  usePOSPsLookup,
  useUpdatePolicyStatus,
  usePolicyTimeline,
} from '@/hooks';
import {
  usePolicyInstallments,
  useGenerateInstallments,
  useRecordInstallmentPayment,
  useReinstatePolicy,
} from '@/hooks/usePolicies';
import type { InstallmentSummary, PolicyInstallment } from '@/services/policies';
import { useRecalculateCommission } from '@/hooks/useCommissions';
import { describeRecalcOutcome, type RecalcNotice } from '@/services/commissionRecalc';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { policyService, getErrorMessage } from '@/services';
import { formatDate, formatCurrency, titleCase } from '@/utils';
import { useAuthStore } from '@/store';
import { useCan } from '@/hooks/useEmployeeRights';
import { lifecycleBadgeLabel, type CommissionBreakdown } from '@/types';
import {
  buildPremiumGroups,
  buildVehicleGroups,
  type DetailGroup,
  type PolicyWithBreakdown,
} from './policyDetailFields';
import { pospOptionLabel } from './pospOptionLabel';
import { updatePolicyPOSP } from './pospAssignment';
import { PolicyAdvisorCard } from './PolicyAdvisorCard';
import { paymentStageStatus, paymentHistoryRows, emptyHistoryMessage } from './paymentStage';
import { RenewNowCard } from './RenewNowCard';

/**
 * #243 -- the types a browser renders without executing anything.
 *
 * Kept identical to `INLINE_SAFE_TYPES` in PolicyDocumentViewSet.download
 * (backend/apps/policies/views.py). The server already refuses to send
 * anything else inline; this repeats the check client-side because View hands
 * the blob to a tab on our own origin, where active content would execute.
 * image/svg+xml is excluded on both sides -- an SVG can carry <script>.
 */
const INLINE_SAFE_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff',
  'text/plain',
]);

const DOCUMENT_TYPE_OPTIONS = [
  { value: 'policy_copy', label: 'Policy Copy' },
  { value: 'schedule', label: 'Schedule' },
  { value: 'rc_copy', label: 'RC Copy' },
  { value: 'aadhar', label: 'Aadhar' },
  { value: 'pan', label: 'PAN' },
  { value: 'kyc_other', label: 'Other KYC' },
  { value: 'id_proof', label: 'ID Proof' },
  { value: 'address_proof', label: 'Address Proof' },
  { value: 'vehicle_rc', label: 'Vehicle RC' },
  { value: 'previous_policy', label: 'Previous Policy' },
  { value: 'inspection_report', label: 'Inspection Report' },
  { value: 'other', label: 'Other' },
];

export function PolicyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  // POSP-3 / CUSTOMER-9: a POSP and a customer both get a read-only policy --
  // no Edit, no Recalculate Commission, no Upload Document. They keep View and
  // Download on the documents the Broker Admin uploaded. Enforced server-side
  // by PolicyViewSet.BROKER_ONLY_WRITE_ACTIONS.
  // Insured members are hidden from insurer roles server-side (a safe default
  // pending an owner decision), so the card would only ever say "none recorded".
  const isInsurerRole = user?.role === 'insurer_admin' || user?.role === 'insurer_employee';
  const isReadOnlyPolicyRole =
    user?.role === 'posp' || user?.role === 'super_posp' || user?.role === 'customer';
  // B4: every policy write below is policies Edit on the API; "Record renewal
  // policy" is policies Create and "Create Renewal Quote" is quotes Create.
  const hasEditPolicyRight = useCan('policies', 'edit');
  const hasCreatePolicyRight = useCan('policies', 'create');
  const hasCreateQuoteRight = useCan('quotes', 'create');
  // B4 review fix: `EntityLink` only gates `kind="posp"` on a role list AND a
  // rights cell (see EntityLink.tsx) -- every other kind it renders (insurer,
  // contact, quote) resolves to a live href for ANY viewer, regardless of
  // whether the destination route would actually admit them. That file is
  // outside this lane's domain, so the same role-list-AND-rights check is
  // done here, at the call site, before EntityLink is even reached: a viewer
  // who fails it never gets a href, so EntityLink renders its already-correct
  // plain-text fallback (id or role stay meaningful, only the wiring differs
  // from the POSP case, where the check lives inside EntityLink itself).
  const hasContactsReadRight = useCan('contacts', 'read');
  const hasQuotesReadRight = useCan('quotes', 'read');
  // Mirrors App.tsx's `/contacts/:id` allowedRoles exactly.
  const canViewContact =
    !!user?.role &&
    ['broker_admin', 'broker_employee', 'posp', 'super_posp'].includes(user.role) &&
    hasContactsReadRight;
  // Mirrors App.tsx's `/quotes/:id` allowedRoles exactly.
  const canViewSourceQuote =
    !!user?.role &&
    ['broker_admin', 'broker_employee', 'posp', 'super_posp', 'insurer_admin', 'insurer_employee'].includes(
      user.role
    ) &&
    hasQuotesReadRight;
  // Mirrors App.tsx's `/broker/insurers/:id/commissions` allowedRoles exactly
  // -- that route carries no `requires`, so role alone is the whole gate.
  const canViewInsurerCommissions = user?.role === 'broker_admin';
  // Mirrors App.tsx's `/quotes/new` allowedRoles exactly -- combined with
  // `hasCreateQuoteRight` below, which answers the `quotes: create` cell that
  // route also requires.
  const canCreateRenewalQuote =
    !!user?.role &&
    ['broker_admin', 'broker_employee', 'posp', 'super_posp'].includes(user.role) &&
    hasCreateQuoteRight;
  const canEditPolicy = user?.role !== 'country_admin' && !isReadOnlyPolicyRole && hasEditPolicyRight;
  // Board #279: archiving IS a policy write, so it follows the same rule as
  // Edit -- a country admin oversees the book and never writes to it, and
  // `PolicyWritesAreBrokerSide` is what actually enforces that.
  const canArchivePolicy = canEditPolicy;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [showUploadForm, setShowUploadForm] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('policy_copy');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // #251 -- change / detach the agent on an issued policy. Broker-admin only,
  // matching PolicyViewSet.POSP_ASSIGNMENT_ACTIONS on the server (broker_admin
  // and super_admin; a country admin never writes to the book, board #9).
  // This flag only decides whether the panel RENDERS -- the payout guard, the
  // cross-broker check and the propagation all live on the server, and the
  // plain edit form's PATCH is guarded there too. Hiding a control is not what
  // stops a detach; PolicyViewSet.perform_update is.
  //
  // The panel is a separate component so its POSP lookup only fires for the
  // roles that can use it, instead of on every customer's policy page.
  const canManagePOSP = user?.role === 'broker_admin' || user?.role === 'super_admin';

  const { data: policy, isLoading, error } = usePolicy(id!);
  // Insurer roles are refused documents, payments and the activity log (403),
  // so those requests are not made for them and their cards are not drawn.
  const { data: documents } = usePolicyDocuments(id!, !isInsurerRole);
  const { data: payments } = usePolicyPayments(id!, !isInsurerRole);
  // #632: same query as the schedule panel below, so it is one request. Used
  // to show a premium settled at issue (no receipt row) in Payment History.
  const { data: scheduleData } = usePolicyInstallments(id!, {
    enabled:
      !isInsurerRole && !!(policy as unknown as { is_installment_policy?: boolean } | undefined)?.is_installment_policy,
  });
  const historyRows = paymentHistoryRows(payments?.results, scheduleData?.schedule);
  const recalcCommission = useRecalculateCommission();
  // W0-A (B3): what the last recalculation actually did -- "unchanged, nothing
  // posted" or "already paid, so a clawback/adjustment of ₹X was posted" --
  // shown on the card rather than a blanket "recalculated".
  const [recalcNotice, setRecalcNotice] = useState<RecalcNotice | null>(null);
  // Broker-side staff only -- not a POSP, not the customer. This used to be
  // named `canMarkPaymentReceived` and gate the payment button as well as the
  // status modal; the payment button is gone, so the name now says what it
  // actually decides.
  const canManagePolicyStatus =
    (user?.role === 'super_admin' ||
      user?.role === 'country_admin' ||
      user?.role === 'broker_admin' ||
      user?.role === 'broker_employee') &&
    hasEditPolicyRight;

  // Moving a policy's status is a broker-side action, same set the backend
  // lets touch the accounting flag above.
  const canUpdateStatus = canManagePolicyStatus;
  const updateStatus = useUpdatePolicyStatus();
  const { data: timeline } = usePolicyTimeline(id!, !isInsurerRole);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: '', notes: '' });

  const openStatusModal = () => {
    setStatusForm({ status: policy?.status ?? '', notes: '' });
    setShowStatusModal(true);
  };

  const submitStatus = () => {
    if (!statusForm.status) return;
    updateStatus.mutate(
      { id: id!, status: statusForm.status, notes: statusForm.notes.trim() },
      { onSuccess: () => setShowStatusModal(false) }
    );
  };

  // The public link. `share` returns a path; the absolute URL is built here so
  // it works behind whatever host the SPA is served from.
  const shareMutation = useMutation({
    mutationFn: () => policyService.sharePolicy(id!),
    onSuccess: async (data) => {
      const url = `${window.location.origin}${data.public_url}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Public link copied to clipboard');
      } catch {
        // Clipboard access is denied outside a secure context and in some
        // browsers. Showing the link beats claiming it was copied.
        toast.success(`Public link: ${url}`);
      }
      queryClient.invalidateQueries({ queryKey: ['policy', id] });
      queryClient.invalidateQueries({ queryKey: ['policy-timeline', id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  // #259: publish AND mail the link. Separate from `shareMutation` because the
  // outcomes differ -- this one reports what the mail server actually did, and
  // a 502 (link published, mail refused) must not read as a success.
  const emailLinkMutation = useMutation({
    mutationFn: () => policyService.sharePolicy(id!, { send_to_email: true }),
    onSuccess: (data) => {
      toast.success(`Policy link sent to ${data.recipient ?? 'the policy holder'}`);
      queryClient.invalidateQueries({ queryKey: ['policy', id] });
      queryClient.invalidateQueries({ queryKey: ['policy-timeline', id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  // Board #279. Never a delete: the policy keeps every commission row, ledger
  // entry and report line it had; it just leaves the default Policies list.
  const archiveMutation = useMutation({
    mutationFn: (archived: boolean) =>
      archived ? policyService.archivePolicy(id!) : policyService.unarchivePolicy(id!),
    onSuccess: (updated) => {
      // Report the STORED flag the server sent back, not the one that was
      // requested -- a success message over an unchanged row is exactly the
      // failure this codebase keeps producing.
      toast.success(updated.is_archived ? 'Policy archived.' : 'Policy restored from archive.');
      queryClient.invalidateQueries({ queryKey: ['policy', id] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.invalidateQueries({ queryKey: ['policy-timeline', id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) => policyService.uploadPolicyDocument(id!, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policyDocuments', id] });
      toast.success('Document uploaded successfully');
      setShowUploadForm(false);
      setSelectedFile(null);
      setSelectedDocType('policy_copy');
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (documentId: string) => policyService.deletePolicyDocument(id!, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policyDocuments', id] });
      toast.success('Document deleted');
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    },
  });

  const downloadDocumentFile = async (documentId: string, name?: string) => {
    try {
      const blob = await policyService.downloadPolicyDocument(id!, documentId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'policy-document';
      // Anchor must be in the DOM for the download to fire in some browsers,
      // and the object URL must not be revoked until after the click resolves.
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDownloadPolicy = () => {
    const docs = documents?.results ?? [];
    if (docs.length === 0) {
      toast.error('No document uploaded for this policy yet.');
      return;
    }
    const doc = docs.find((d) => d.document_type === 'policy_copy') ?? docs[0];
    downloadDocumentFile(doc.id, doc.name);
  };

  // #243: View opens the document in a new browser tab. Download (above) is
  // deliberately untouched.
  //
  // Three things this has to get right, all of them learned the hard way:
  //
  // 1. The tab is opened SYNCHRONOUSLY, before the await. A window.open() that
  //    runs after an awaited fetch is outside the user-gesture window, so
  //    browsers block it silently -- View would appear to do nothing at all.
  //    jsdom has no popup blocker, so tests cannot catch that regression;
  //    the ordering is pinned by a test instead.
  // 2. 'noopener' is NOT passed in the features string: per spec that makes
  //    window.open return null, which is exactly the handle we need. The
  //    destination is a same-origin blob: URL of our own making, so there is
  //    no untrusted-opener surface to close.
  // 3. The type allowlist is re-applied here on the blob we actually received.
  //    A blob: URL inherits THIS origin, so pointing a tab at a text/html or
  //    SVG blob would run the uploader's markup on our domain -- the precise
  //    risk PolicyDocumentViewSet.download's INLINE_SAFE_TYPES set exists to
  //    prevent. The earlier version of View was a bare window.open of a blob
  //    URL and the browser downloaded it anyway whenever mime_type was blank;
  //    the server now guesses the type, and anything it will not serve inline
  //    is downloaded here rather than shown in an empty tab.
  const handleViewDocument = async (documentId: string, name?: string) => {
    const tab = window.open('', '_blank');
    if (!tab) {
      toast.error('Your browser blocked the document tab. Allow pop-ups for this site to view documents.');
      return;
    }
    // Severing opener costs nothing and keeps the new tab independent.
    try {
      tab.opener = null;
    } catch {
      // Some browsers make `opener` read-only; harmless either way.
    }
    try {
      const blob = await policyService.downloadPolicyDocument(id!, documentId, true);
      const type = (blob.type || '').split(';')[0].trim().toLowerCase();
      if (!INLINE_SAFE_TYPES.has(type)) {
        tab.close();
        toast.error('This file type cannot be previewed. Downloading it instead.');
        await downloadDocumentFile(documentId, name);
        return;
      }
      const url = URL.createObjectURL(blob);
      tab.location.href = url;
      // Revoking straight away leaves the new tab blank -- it has not loaded
      // the blob yet. Same pattern as downloadDocumentFile's 2s timer, with
      // room for a slow render.
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      // Never leave a blank tab standing after a failed fetch.
      tab.close();
      toast.error(getErrorMessage(err));
    }
  };

  const handleDeleteDocument = (documentId: string, docName: string) => {
    if (window.confirm(`Delete document "${docName}"?`)) {
      deleteMutation.mutate(documentId);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    const formData = new FormData();
    formData.append('document_type', selectedDocType);
    formData.append('file', selectedFile);
    formData.append('name', selectedFile.name);
    uploadMutation.mutate(formData);
  };

  if (isLoading) {
    return <ContentLoader />;
  }

  if (error || !policy) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Policy not found</h2>
        <p className="text-gray-500 mt-2 dark:text-gray-400">The policy you are looking for does not exist.</p>
        <Link to="/policies" className="mt-4 inline-block">
          <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
            Back to Policies
          </Button>
        </Link>
      </div>
    );
  }

  // Make/model/variant are all OPTIONAL on the Add Policy form, so gating on
  // them alone hid the whole card for a motor policy entered with just a
  // registration number -- the number showed in the list and in search, then
  // vanished on the page it belongs to. The flat columns count as vehicle data.
  const vehicleFlat = policy as PolicyWithBreakdown;
  const hasVehicleDetails = Boolean(
    policy.vehicle_details ||
      policy.vehicle_make_name ||
      policy.vehicle_model_name ||
      policy.vehicle_variant_name ||
      vehicleFlat.vehicle_number ||
      vehicleFlat.registration_date ||
      vehicleFlat.manufacturing_year
  );

  // The detail endpoint returns the whole premium breakdown and the whole
  // PolicyVehicle row; `Policy` in src/types only declares part of it (that
  // file is outside this task's domain — see policyDetailFields.ts).
  const detail = policy as PolicyWithBreakdown;
  const premiumGroups = buildPremiumGroups(detail);
  const vehicleGroups = buildVehicleGroups(detail);

  // Point 13: the commission card must render whenever the viewer is allowed
  // to see commissions, not only when a calculation already exists -- that
  // conflation is exactly what hid the Recalculate button on never-calculated
  // policies. `commission` is the backend's role gate: it is `null` for a
  // viewer with no commission rights at all (see PolicyDetailSerializer.
  // get_commission / commission_summary_for) and a present object -- possibly
  // every figure "0.00" -- for every role that IS permitted to see them, even
  // with zero calculations. So presence/absence of the key is the correct
  // permission check; its CONTENTS cannot also tell "never calculated" apart
  // from "calculated, genuinely zero" (has_commission_calculation does that).
  const canViewCommission = policy.commission != null;

  // B9: "Record renewal policy" -- a policy create, so not for a country admin
  // (or a customer, whose Quick Actions card is hidden anyway).
  const canRecordRenewalPolicy =
    user?.role !== 'customer' &&
    user?.role !== 'country_admin' &&
    hasCreatePolicyRight &&
    !!policy.lifecycle_status &&
    !policy.renewed_by;

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/policies" />
      </div>
      <PageHeader
        title={
          // U-05: the insurer's mark beside the number, as on the Policies
          // list; its blurb (when there is one) rides in the hover title.
          <span className="flex items-center gap-3">
            <InsurerLogo
              src={policy.insurer_logo_url}
              name={policy.insurer_name}
              description={policy.insurer_description}
              size="md"
            />
            <span className="min-w-0">{policyNumberLabel(policy)}</span>
          </span>
        }
        subtitle={[policy.product_name, policy.insurer_name].filter(Boolean).join(' - ')}
        breadcrumbs={[
          { label: 'Policies', href: '/policies' },
          { label: policyNumberLabel(policy) },
        ]}
        actions={
          <div className="flex items-center gap-3">
            {/* B4 review fix: `/claims/new`'s allowedRoles (App.tsx) admit
                broker_admin and super_admin but NOT country_admin -- a
                country admin oversees the book and never writes to it (board
                #9), same as Edit/Archive above, and used to get a "Raise
                Claim" button that only led to /unauthorized. */}
            {((user?.role === 'customer' && policy?.lifecycle_status === 'active') ||
              user?.role === 'broker_admin' ||
              user?.role === 'super_admin') && (
              <Link to={`/claims/new?policyId=${policy.id}`}>
                <Button variant="primary">Raise Claim</Button>
              </Link>
            )}
            {!isInsurerRole && (
              <Button
                variant="outline"
                onClick={handleDownloadPolicy}
                leftIcon={<DocumentArrowDownIcon className="w-5 h-5" />}
              >
                Download
              </Button>
            )}
            {canArchivePolicy && (
              <Button
                variant="outline"
                isLoading={archiveMutation.isPending}
                onClick={() => archiveMutation.mutate(!policy.is_archived)}
              >
                {policy.is_archived ? 'Restore' : 'Archive'}
              </Button>
            )}
            {canEditPolicy && (
              <Link to={`/policies/${id}/edit`}>
                <Button leftIcon={<PencilIcon className="w-5 h-5" />}>Edit Policy</Button>
              </Link>
            )}
          </div>
        }
      />

      {/* Board #279: an archived policy is fully readable, so the page has to
          say why it is missing from the Policies list. */}
      {policy.is_archived && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          <Badge variant="default" size="sm">Archived</Badge>
          <span>This policy is hidden from the default Policies list. Nothing has been deleted.</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Policy Details */}
          <Card>
            <CardHeader title="Policy Details" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <InfoItem label="Policy Number" value={<PolicyNumber source={policy} />} />
              <InfoItem label="Product" value={policy.product_name} />
              <InfoItem
                label="Insurer"
                // B4 review fix: `/broker/insurers/:id/commissions` is
                // broker_admin-only (App.tsx); EntityLink's `insurer` branch
                // does not gate on role, so every other viewer on this page
                // (customer included) used to get a live link into an admin
                // page it would immediately be bounced out of.
                value={
                  canViewInsurerCommissions ? (
                    <EntityLink kind="insurer" id={policy.insurer} name={policy.insurer_name} />
                  ) : (
                    policy.insurer_name
                  )
                }
              />
              {user?.role !== 'broker_admin' && user?.role !== 'broker_employee' && (
                <InfoItem label="Broker" value={policy.broker_name} />
              )}
              <div>
                <InfoItem
                  label="POSP"
                  // B4 review fix: this used to be a raw `to=` link gated only
                  // on role (broker_employee excluded), duplicating -- and
                  // drifting from -- the D2 role list, and ignoring the
                  // `channels: read` cell the route also requires. `EntityLink`
                  // is the one place that check lives now: it renders the
                  // shared PospEntityLink, which resolves the role list AND
                  // the rights cell and falls back to plain text (or a retry
                  // affordance) whenever either would land on /unauthorized.
                  value={
                    <EntityLink
                      kind="posp"
                      id={policy.posp}
                      name={policy.posp_name || 'Not Assigned'}
                    />
                  }
                />
                {canManagePOSP && (
                  <POSPAssignmentPanel
                    policyId={id!}
                    currentPospId={policy.posp ?? null}
                    onChanged={() => {
                      queryClient.invalidateQueries({ queryKey: ['policy', id] });
                      queryClient.invalidateQueries({ queryKey: ['policies'] });
                    }}
                  />
                )}
              </div>
              <InfoItem label="Policy Term" value={`${policy.policy_term_months} months`} />
              {/* B9 (a/f): read from the server-derived `business_type`
                  (falling back to `is_renewal` for an older payload). */}
              <InfoItem
                label="Business Type"
                value={
                  (policy.business_type ?? (policy.is_renewal ? 'renewal' : 'new')) === 'renewal'
                    ? 'Renewal'
                    : 'New Business'
                }
              />
              {/* B9: the explicit renewal link, both directions. */}
              {policy.previous_policy && (
                <InfoItem
                  label="Renewal of"
                  value={policyNumberLabel({ display_policy_number: policy.previous_policy_number }, 'Previous policy')}
                  to={`/policies/${policy.previous_policy}`}
                />
              )}
              {policy.renewed_by && (
                <InfoItem
                  label="Renewed by"
                  value={<PolicyNumber source={policy.renewed_by} />}
                  to={`/policies/${policy.renewed_by.id}`}
                />
              )}
              {/* Source-quote threading (round-2 Wave F3, item 10) -- only
                  present when this policy was actually created from a quote
                  (broker-entered policies have no `quote`). Links to the
                  parent QuoteRequest detail page (`/quotes/:id`), since
                  there's no standalone per-insurer Quote detail page. */}
              {policy.quote_number && (
                <InfoItem
                  label="Source Quotation"
                  // B4 review fix: `/quotes/:id` admits broker_admin,
                  // broker_employee, posp, super_posp, insurer_admin and
                  // insurer_employee -- NOT customer or country_admin, both of
                  // whom can view this policy. EntityLink's `quote` branch
                  // does not gate on role or on the `quotes: read` cell it
                  // also requires, so either of those viewers used to get a
                  // live link straight to /unauthorized.
                  value={
                    canViewSourceQuote ? (
                      <EntityLink
                        kind="quote"
                        id={policy.quote_request_id ?? undefined}
                        name={`Quoted from ${policy.quote_number}`}
                      />
                    ) : (
                      `Quoted from ${policy.quote_number}`
                    )
                  }
                />
              )}
            </div>
            {/* A6/#494: created / updated stamps live in the Policy Details card. */}
            <RecordTimestamps
              className="mt-4 border-t border-gray-100 pt-3 dark:border-gray-700"
              createdAt={policy.created_at}
              updatedAt={policy.updated_at}
            />
          </Card>

          {/* Policy Holder */}
          <Card>
            <CardHeader title="Policy Holder" />
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                <UserIcon className="w-6 h-6 text-primary-600" />
              </div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Customer Name</p>
                  {/* B4 review fix: `/contacts/:id` admits broker_admin,
                      broker_employee, posp and super_posp -- NOT customer,
                      country_admin, insurer_admin or insurer_employee, all of
                      whom can view this policy. EntityLink's `contact` branch
                      does not gate on role or on the `contacts: read` cell it
                      also requires (an ordinary editable cell a broker admin
                      can revoke per employee), so any of those viewers used
                      to get a live link straight to /unauthorized. */}
                  {canViewContact ? (
                    <EntityLink
                      kind="contact"
                      id={policy.customer ?? undefined}
                      name={policy.holder_name}
                      className="text-sm font-medium text-primary-600 hover:underline mt-0.5 inline-block dark:text-primary-400"
                    />
                  ) : (
                    <p className="text-sm font-medium text-gray-900 mt-0.5 dark:text-gray-100">{policy.holder_name}</p>
                  )}
                </div>
                <InfoItem label="Phone" value={policy.holder_phone} />
                <InfoItem label="Email" value={policy.holder_email || '-'} />
                {/* A company has no date of birth and no gender, so Add Policy
                    stops asking a corporate holder for either (Task 8). This
                    card went on rendering both as an empty "-" regardless,
                    which reads as missing data on a record that is complete.
                    A row with nothing in it is not shown at all -- for any
                    holder, not just corporate ones: an individual whose date of
                    birth was never captured is in exactly the same position. */}
                {policy.holder_dob && (
                  <InfoItem label="Date of Birth" value={formatDate(policy.holder_dob)} />
                )}
                {policy.holder_gender && (
                  <InfoItem label="Gender" value={titleCase(policy.holder_gender)} />
                )}
                {policy.holder_pincode && (
                  <InfoItem label="Pincode" value={policy.holder_pincode} />
                )}
                {policy.holder_address && (
                  <div className="sm:col-span-2">
                    <InfoItem label="Address" value={policy.holder_address} />
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Premium Details — one uniform list of label/value rows (A5). The
              headline three used to sit above these as centred boxed tiles,
              which made a single card speak two visual languages; they are now
              just the leading group built by buildPremiumGroups.

              Components default to 0 in the database, so a zero there means
              "does not apply to this policy" (a health policy carries
              od_premium=0.00) and is hidden rather than shown as ₹0. The
              headline three are the exception — see premiumAmountAlways. */}
          <Card>
            <CardHeader title="Premium Details" />
            <div className="space-y-5">
              {premiumGroups.map((group) => (
                <FieldGroup key={group.title} group={group} />
              ))}
            </div>
          </Card>

          {/* #332: health insured members / master policy. Null structure =
              not a health policy, so nothing renders. */}
          {policy.member_structure && !isInsurerRole && (
            <PolicyMembersCard
              policy={policy}
              editHref={canEditPolicy ? `/policies/${id}/edit` : undefined}
            />
          )}

          {/* Vehicle Details (round-2 Wave C, item C3) — motor policies only.
              Detected via presence of vehicle data itself (nested PolicyVehicle
              row, or a flat FK name), which is only ever populated for motor
              policies — robust across both the quote-originated and
              broker-entered (flat-FK) paths. */}
          {hasVehicleDetails && (
            <Card>
              <CardHeader title="Vehicle Details" />
              {/* Grouped rather than one flat list of 22 fields: identity,
                  registration, specification, financing and accessories each
                  answer a different question. Groups with nothing in them
                  (no financing, no accessories) are dropped entirely. */}
              <div className="space-y-5">
                {vehicleGroups.map((group) => (
                  <FieldGroup key={group.title} group={group} />
                ))}
              </div>
            </Card>
          )}

          {/* Commission (role-gated). The card renders whenever the user may see
              commissions -- NOT only when a calculation already exists. Gating the
              whole block on `policy.commission` hid the Recalculate button on
              exactly the policies that most needed it: the ones never calculated
              (point 13). */}
          {canViewCommission && (
            <Card>
              <CardHeader
                title="Commission"
                action={
                  // POSP-3: a POSP sees their commission but cannot trigger a
                  // recalculation -- that is the Broker Admin's action.
                  isReadOnlyPolicyRole ? undefined : (
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<ArrowPathIcon className="w-4 h-4" />}
                      isLoading={recalcCommission.isPending}
                      onClick={() => {
                        setRecalcNotice(null);
                        recalcCommission.mutate(id!, {
                          onSuccess: (result) => setRecalcNotice(describeRecalcOutcome(result)),
                        });
                      }}
                    >
                      {policy.has_commission_calculation ? 'Recalculate Commission' : 'Calculate Commission'}
                    </Button>
                  )
                }
              />
              {recalcNotice && (
                <div
                  role="status"
                  data-testid="recalc-notice"
                  className={`mb-4 rounded-md border px-3 py-2 text-sm ${
                    recalcNotice.tone === 'warning'
                      ? 'border-amber-300 bg-amber-50 text-amber-900'
                      : recalcNotice.tone === 'info'
                        ? 'border-blue-200 bg-blue-50 text-blue-900'
                        : 'border-green-200 bg-green-50 text-green-900'
                  }`}
                >
                  {recalcNotice.message}
                </div>
              )}
              {!policy.has_commission_calculation ? (
                <EmptyState title="No commission calculated for this policy yet." />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {policy.commission?.received !== undefined && (
                    <InfoItem label="Received from Insurer" value={formatCurrency(policy.commission.received)} />
                  )}
                  {policy.commission?.paid_to_posp !== undefined && (
                    <InfoItem label="Paid to POSP" value={formatCurrency(policy.commission.paid_to_posp)} />
                  )}
                  {policy.commission?.paid_to_advertiser !== undefined && (
                    <InfoItem
                      label="Paid to Advertiser"
                      value={formatCurrency(policy.commission.paid_to_advertiser)}
                    />
                  )}
                  {policy.commission?.broker !== undefined && (
                    <InfoItem label="Broker" value={formatCurrency(policy.commission.broker)} />
                  )}
                  {policy.commission?.margin !== undefined && (
                    <InfoItem label="Margin" value={formatCurrency(policy.commission.margin)} />
                  )}
                  {policy.commission?.your_commission !== undefined && (
                    <InfoItem label="Your Commission" value={formatCurrency(policy.commission.your_commission)} />
                  )}
                  {policy.commission?.downline_commission !== undefined && (
                    <InfoItem label="Downline" value={formatCurrency(policy.commission.downline_commission)} />
                  )}
                </div>
              )}
              {policy.commission?.commission_breakdown && (
                <CommissionBreakdownTables breakdown={policy.commission.commission_breakdown} />
              )}
              {policy.commission?.rate_source?.summary && (
                <div className="mt-4 text-sm text-gray-700 dark:text-gray-300" data-testid="commission-rate-source">
                  <p>{policy.commission.rate_source.summary}</p>
                  {policy.commission.rate_source.incremental?.summary && (
                    <p className="mt-1" data-testid="commission-incremental-slab">
                      {policy.commission.rate_source.incremental.summary}
                    </p>
                  )}
                  {(policy.commission.rate_source.conditions_shown ?? []).length > 0 && (
                    <ul className="mt-1 list-disc pl-5 text-xs text-gray-500 dark:text-gray-400">
                      {(policy.commission.rate_source.conditions_shown ?? []).map((c, i) => (
                        <li key={i}>
                          {c.text} (shown, not enforced)
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Documents */}
          {!isInsurerRole && (
            <Card>
              <CardHeader
                title="Documents"
                action={
                  // POSP-3 / CUSTOMER-9: documents are uploaded by the Broker
                  // Admin. A POSP or customer only views and downloads them.
                  isReadOnlyPolicyRole ? undefined : (
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<ArrowUpTrayIcon className="w-4 h-4" />}
                      onClick={() => setShowUploadForm(!showUploadForm)}
                    >
                      Upload Document
                    </Button>
                  )
                }
              />

              {showUploadForm && !isReadOnlyPolicyRole && (
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 space-y-3">
                  <SearchableSelect
                    label="Document Type"
                    value={selectedDocType}
                    onChange={setSelectedDocType}
                    options={DOCUMENT_TYPE_OPTIONS}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Select File
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:text-gray-400"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      size="sm"
                      onClick={handleUpload}
                      isLoading={uploadMutation.isPending}
                    >
                      Upload
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowUploadForm(false);
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {documents?.results && documents.results.length > 0 ? (
                <div className="space-y-3">
                  {documents.results.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <DocumentArrowDownIcon className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{doc.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {doc.document_type_label || titleCase(doc.document_type)}
                            {doc.created_at && (
                              <span className="ml-2 text-gray-500 dark:text-gray-400">
                                {formatDate(doc.created_at)}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleViewDocument(doc.id, doc.name)}>
                          View
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => downloadDocumentFile(doc.id, doc.name)}>
                          Download
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDocument(doc.id, doc.name)}
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No documents uploaded</p>
              )}
            </Card>
          )}

          {/* Premium Paying Term.
              Rendered only for a policy that IS paid in installments -- the
              flag comes off the policy payload, so an ordinary motor policy
              never fires the schedule request at all. */}
          {(policy as unknown as { is_installment_policy?: boolean }).is_installment_policy && (
            <InstallmentSchedulePanel
              policyId={id!}
              policyNumber={policyNumberLabel(policy)}
              canManage={canEditPolicy}
            />
          )}

          {/* Payments */}
          {!isInsurerRole && (
            <Card>
              <CardHeader title="Payment History" />
              {historyRows.length > 0 ? (
                <div className="overflow-x-auto -mx-6">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-100 dark:border-gray-700">
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                          Mode
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                      {historyRows.map((payment) => (
                        <tr key={payment.id}>
                          <td className="px-6 py-3 text-sm text-gray-900 dark:text-gray-100">
                            {payment.payment_date ? formatDate(payment.payment_date) : '-'}
                          </td>
                          <td className="px-6 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                            {formatCurrency(payment.amount)}
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {payment.payment_mode_label || payment.note || '-'}
                          </td>
                          <td className="px-6 py-3">
                            <Badge variant={getStatusBadgeVariant(payment.payment_status)} size="sm">
                              {titleCase(payment.payment_status)}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  {emptyHistoryMessage(
                    policy.status,
                    historyRows,
                    !!(policy as unknown as { is_installment_policy?: boolean }).is_installment_policy
                  )}
                </p>
              )}
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* #345: Renew now on the insurer's own payment page (the policy's customer only). */}
          {user?.role === 'customer' && <RenewNowCard policyId={id!} insurerName={policy.insurer_name} />}

          {/* Status Card */}
          <Card>
            <CardHeader title="Status" />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Policy Status</span>
                {/* B9 (c): computed lifecycle, never the stored status. */}
                {/* #632: a draft / pending-payment policy has no lifecycle; show its stored stage rather than an empty dash. */}
                <Badge variant={getStatusBadgeVariant(policy.lifecycle_status ?? policy.status ?? '')}>
                  {policy.lifecycle_status ? lifecycleBadgeLabel(policy) : titleCase(policy.status) || '—'}
                </Badge>
              </div>
              {/* #391: customers see the fact, per stage, derived from the payment rows. */}
              {user?.role === 'customer' && paymentStageStatus(policy.status, historyRows) && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Payment</span>
                  <Badge
                    variant={getStatusBadgeVariant(paymentStageStatus(policy.status, historyRows)!.badge)}
                  >
                    {paymentStageStatus(policy.status, historyRows)!.label}
                  </Badge>
                </div>
              )}
              {/* Payment Status and "Mark Payment Received" are deliberately
                  gone from this card. A policy is issued only once payment has
                  gone through, so a second paid/unpaid state on the policy was
                  a duplicate of that fact and could disagree with it. The
                  column is untouched in the database and the Payments tab below
                  still shows every payment and its own status -- this removes
                  the confusing summary, not the record. */}
              {canUpdateStatus && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-center"
                  onClick={openStatusModal}
                >
                  Update Status
                </Button>
              )}
              {canUpdateStatus && policy.status !== 'draft' && policy.status !== 'cancelled' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-center"
                  isLoading={shareMutation.isPending}
                  onClick={() => shareMutation.mutate()}
                >
                  Copy Public Link
                </Button>
              )}
              {canUpdateStatus && policy.status !== 'draft' && policy.status !== 'cancelled' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-center"
                  isLoading={emailLinkMutation.isPending}
                  disabled={!policy.holder_email}
                  title={
                    policy.holder_email
                      ? `Email the link to ${policy.holder_email}`
                      : 'This policy has no holder email address'
                  }
                  onClick={() => emailLinkMutation.mutate()}
                >
                  Email Public Link
                </Button>
              )}
            </div>
          </Card>

          {/* History. Everything the policy's activity log has recorded --
              status changes and the remark that explained them, agent
              assignment, customer linking -- alongside issuance, payments and
              claims. Those log rows have been written since the model existed
              and were shown nowhere.

              Broker-side only. The server is the boundary (`_ACTIVITY_LOG_ROLES`
              in policies/views.py withholds the log rows from a customer or an
              agent, both of whom can open this page); this gate keeps the empty
              card from rendering for them and keeps the two in step. */}
          {canUpdateStatus && timeline && timeline.length > 0 && (
            <Card>
              <CardHeader title="History" />
              <ol className="space-y-4">
                {timeline.map((entry, index) => (
                  <li key={`${entry.date}-${index}`} className="text-sm">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {entry.event}
                      </span>
                      <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(entry.date)}
                      </span>
                    </div>
                    {entry.details && (
                      <p className="mt-0.5 text-gray-600 dark:text-gray-300 break-words">
                        {entry.details}
                      </p>
                    )}
                    {entry.by && (
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        by {entry.by}
                        {entry.by_role ? ` (${titleCase(entry.by_role)})` : ''}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            </Card>
          )}

          {/* Dates Card */}
          <Card>
            <CardHeader title="Policy Period" />
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CalendarIcon className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Inception Date</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {formatDate(policy.inception_date)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <CalendarIcon className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Expiry Date</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {formatDate(policy.expiry_date)}
                  </p>
                </div>
              </div>
              {policy.grace_period && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <CalendarIcon className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Grace Period</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatDate(policy.grace_period)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Insurer Card */}
          <Card>
            <CardHeader title="Insurer" />
            {/* U-05: the real logo where a placeholder icon used to sit, and
                the insurer's blurb under the product when it has one. */}
            <div className="flex items-center gap-3">
              <InsurerLogo
                src={policy.insurer_logo_url}
                name={policy.insurer_name}
                description={policy.insurer_description}
                size="md"
              />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {canViewInsurerCommissions ? (
                    <EntityLink kind="insurer" id={policy.insurer} name={policy.insurer_name} />
                  ) : (
                    policy.insurer_name
                  )}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{policy.product_name}</p>
                {policy.insurer_description && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{policy.insurer_description}</p>
                )}
              </div>
            </div>
          </Card>

          {/* U-01: the broker and agent for THIS policy -- customers only. */}
          <PolicyAdvisorCard policy={policy} />

          {/* Quick Actions.
              C3: hidden from customers. Its only entry links to
              /quotes/new?renewal=..., a route customers cannot reach, so
              following it landed them on Unauthorized.

              Gated on the customer role specifically, NOT on
              `isReadOnlyPolicyRole` -- that also covers POSPs, and POSP quote
              creation was deliberately restored on 2026-07-25 (POSP-5). Do not
              widen this to POSPs. */}
          {user?.role !== 'customer' && (
            <Card>
              <CardHeader title="Quick Actions" />
              <div className="space-y-3">
                {/* B4 review fix: `/quotes/new`'s allowedRoles (App.tsx)
                    admit broker_admin, broker_employee, posp and super_posp
                    only -- `hasCreateQuoteRight` alone answers the `quotes:
                    create` rights cell, which resolves to `true` for every
                    role OUTSIDE the gated set (country_admin, insurer_admin,
                    insurer_employee included), so this card used to offer the
                    button to three roles the route immediately refuses. */}
                {canCreateRenewalQuote && (
                  <Link to={`/quotes/new?renewal=${id}`} className="block">
                    <Button variant="outline" className="w-full justify-start">
                      Create Renewal Quote
                    </Button>
                  </Link>
                )}
                {/* B9 (f): the live book records most renewals as hand-entered
                    policies, so this is the path that matters. Offered only
                    where it can succeed -- the server refuses a policy that
                    was never issued or is already renewed -- and never to a
                    country admin, who cannot create policies. */}
                {canRecordRenewalPolicy && (
                  <Link to={`/policies/new?renewal=${id}`} className="block">
                    <Button variant="outline" className="w-full justify-start">
                      Record renewal policy
                    </Button>
                  </Link>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>

      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title="Update Policy Status"
      >
        <div className="space-y-4">
          <Select
            label="Status"
            options={policyStatusOptions(policy.status)}
            value={statusForm.status}
            onChange={(e) => setStatusForm((f) => ({ ...f, status: e.target.value }))}
          />
          <div>
            <label
              htmlFor="policy-status-note"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
            >
              Comment
            </label>
            <textarea
              id="policy-status-note"
              rows={3}
              value={statusForm.notes}
              onChange={(e) => setStatusForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Why is the status changing?"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Recorded against the policy and shown in its History.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowStatusModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitStatus}
              disabled={!statusForm.status}
              isLoading={updateStatus.isPending}
            >
              Update Status
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/** Mirrors `Policy.STATUS_CHOICES`, minus the two payment states.
 *
 * A policy is issued only after payment has succeeded, so "Pending Payment"
 * and "Payment Received" are not stages anyone should be able to move a policy
 * INTO from this screen. They remain valid values in the database, and older
 * policies do sit in them, which is why they are not simply deleted here --
 * see `policyStatusOptions` below. */
const POLICY_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending_issuance', label: 'Pending Issuance' },
  { value: 'issued', label: 'Issued' },
  { value: 'active', label: 'Active' },
  { value: 'lapsed', label: 'Lapsed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'expired', label: 'Expired' },
];

/** Withdrawn statuses, kept only so a policy already in one can still be read. */
const WITHDRAWN_STATUS_LABELS: Record<string, string> = {
  pending_payment: 'Pending Payment',
  payment_received: 'Payment Received',
  // Matured is DERIVED from the installment schedule -- it is set when the
  // last premium is recorded, never chosen from this dropdown. It is listed
  // here for the same reason the two payment states are: a matured policy
  // whose status was absent from the select would show a DIFFERENT status in
  // the modal than the one stored, and saving the form would silently change
  // a status nobody asked to change.
  matured: 'Matured',
};

/**
 * The statuses this policy may be set to, plus the one it is already in.
 *
 * Dropping the two payment states outright would have left the policies still
 * sitting in them showing a blank or, worse, a DIFFERENT status in the modal's
 * select than the one actually stored -- and saving that form would then have
 * silently changed the status nobody asked to change.
 */
export function policyStatusOptions(currentStatus?: string | null) {
  if (!currentStatus || !(currentStatus in WITHDRAWN_STATUS_LABELS)) {
    return POLICY_STATUS_OPTIONS;
  }
  return [
    { value: currentStatus, label: `${WITHDRAWN_STATUS_LABELS[currentStatus]} (current)` },
    ...POLICY_STATUS_OPTIONS,
  ];
}

/**
 * #251 — "Change agent" / "Detach agent" for a Broker Admin.
 *
 * Deliberately a separate component: it holds the POSP lookup query, so that
 * request only fires for the roles allowed to reassign, not on every customer's
 * policy page.
 *
 * The refusal path is the point of this panel. When the agent has already been
 * paid, the server answers 400 with an exact sentence and the remark typed here
 * is still recorded against the policy — so the message is rendered verbatim
 * from the server's response rather than re-worded locally, and the remark box
 * stays open with its text intact.
 */
function POSPAssignmentPanel({
  policyId,
  currentPospId,
  onChanged,
}: {
  policyId: string;
  currentPospId: string | null;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  /** The option the user actually clicked, remembered so a later (narrower)
   *  search result cannot make its label vanish — see `options` below. */
  const [pinnedDraft, setPinnedDraft] = useState<{ value: string; label: string } | null>(null);
  const [remark, setRemark] = useState('');
  const [errorText, setErrorText] = useState('');
  // The reassignment picker used to call usePOSPsLookup() with no term, so it
  // only ever held PAGE ONE of /brokers/posps/ (20 rows) — a broker with more
  // active agents than that could not reassign a policy to the rest at all.
  // Typing now drives the endpoint's own SearchFilter, debounced, exactly like
  // the Add Policy form's agent picker.
  const { debouncedTerm: pospSearch, setTerm: setPospSearch } = useDebouncedSearch();
  const { data: posps, isLoading } = usePOSPsLookup(pospSearch || undefined);

  const mutation = useMutation({
    mutationFn: ({ posp }: { posp: string | null }) => updatePolicyPOSP(policyId, posp, remark),
    onSuccess: (data) => {
      toast.success(data.message);
      setOpen(false);
      setDraft('');
      setPinnedDraft(null);
      setRemark('');
      setErrorText('');
      onChanged();
    },
    onError: (err) => {
      // Surface the server's own words. This is the "Agent payment has already
      // been completed. POSP cannot be detached." case, among others.
      const message = getErrorMessage(err);
      setErrorText(message);
      toast.error(message);
    },
  });

  const fetched = [
    { value: '', label: 'Select agent' },
    ...(posps || [])
      .filter((p) => p.id !== currentPospId)
      .map((p) => ({ value: p.id, label: pospOptionLabel(p) })),
  ];
  // `posps` is a SEARCH result now, not the whole directory, and selecting an
  // option closes the popup — which resets the term, so the very next result
  // is page one again and no longer holds an agent that was only reachable by
  // searching. SearchableSelect resolves its displayed label with
  // `options.find(o => o.value === value)`, so without keeping the picked
  // option in the list the closed field falls straight back to the
  // placeholder, i.e. reads as "no agent chosen" one click before Save posts
  // that very agent. Pin it (same technique as PolicyCreatePage's pospOptions).
  const options =
    draft && pinnedDraft?.value === draft && !fetched.some((o) => o.value === draft)
      ? [fetched[0], pinnedDraft, ...fetched.slice(1)]
      : fetched;

  if (!open) {
    return (
      <div className="mt-1 flex items-center gap-3">
        <button
          type="button"
          className="text-xs font-medium text-primary-600 hover:underline"
          onClick={() => {
            setOpen(true);
            setErrorText('');
          }}
        >
          {currentPospId ? 'Change agent' : 'Assign agent'}
        </button>
        {currentPospId && (
          <button
            type="button"
            className="text-xs font-medium text-red-600 hover:underline"
            onClick={() => {
              setOpen(true);
              setDraft('');
              setPinnedDraft(null);
              setErrorText('');
            }}
          >
            Detach agent
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-md border border-gray-200 dark:border-gray-700 p-3 space-y-3">
      <div>
        {/* ui/SearchableSelect renders only the control, so the label is ours.
            `htmlFor` + the trigger's `id` is the same pairing
            common/SearchableSelect does internally. */}
        <label
          htmlFor="posp-reassign"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Agent
        </label>
        <ServerSearchableSelect
          id="posp-reassign"
          options={options}
          value={draft}
          onChange={(v) => {
            const next = v ?? '';
            setDraft(next);
            const picked = options.find((o) => o.value === next);
            setPinnedDraft(next && picked ? { value: next, label: picked.label } : null);
          }}
          onQueryChange={setPospSearch}
          loading={isLoading}
          placeholder="Search and select agent"
        />
      </div>
      <div>
        <label
          htmlFor="posp-remark"
          className="block text-sm text-gray-500 dark:text-gray-400 mb-1"
        >
          Remark (optional)
        </label>
        <textarea
          id="posp-remark"
          rows={2}
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Why is the agent being changed or removed?"
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 px-3 py-2 text-sm"
        />
      </div>
      {errorText && (
        <p role="alert" className="text-sm text-red-600">
          {errorText}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={!draft || mutation.isPending}
          onClick={() => mutation.mutate({ posp: draft })}
        >
          Save agent
        </Button>
        {currentPospId && (
          <Button
            size="sm"
            variant="outline"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate({ posp: null })}
          >
            Detach agent
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          disabled={mutation.isPending}
          onClick={() => {
            setOpen(false);
            setErrorText('');
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

function InfoItem({ label, value, to }: { label: string; value: ReactNode; to?: string }) {
  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      {to ? (
        <Link to={to} className="text-sm font-medium text-primary-600 hover:underline mt-0.5 inline-block dark:text-primary-400">
          {value}
        </Link>
      ) : (
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5 break-words">{value}</p>
      )}
    </div>
  );
}

/** What the insurer's rule actually paid, and what the agent was paid out of it.
 *
 *  The figures were being computed and stored all along -- the engine has
 *  recorded every leg with its percentage and the premium it applied to since
 *  #248 -- and rendered nowhere, so a broker saw "Received from Insurer:
 *  8,311.95" and had no way to check it against the grid they had agreed.
 *
 *  Two tables, because they answer the two questions a broker actually asks:
 *  what came IN from the insurance company, and what goes OUT to the agent. The
 *  agent's legs are the same legs, apportioned -- so the labels line up row for
 *  row and the two can be read side by side.
 */
/**
 * The premium-paying-term schedule for a Life / Health / Term policy.
 *
 * ONE policy, ONE policy number, many installments. Recording a payment here
 * is the renewal of this policy -- it settles the next schedule row against
 * the existing record. Nothing on this panel creates a policy, which is why
 * the policy number is printed above the table: it is the same number before
 * and after every action.
 *
 * Loading, error and empty are three DISTINCT states. A failed request that
 * rendered as "No installments scheduled" would be the page asserting, with
 * authority, that a customer owes nothing.
 */
/** U-01 (24 Sep): rows per page of the schedule. A monthly term runs to
 *  hundreds of installments; a year's worth per page keeps the panel compact. */
const INSTALLMENTS_PER_PAGE = 12;

export function InstallmentSchedulePanel({
  policyId,
  policyNumber,
  canManage,
}: {
  policyId: string;
  policyNumber: string;
  canManage: boolean;
}) {
  const { data, isLoading, isError, error, refetch, isFetching } = usePolicyInstallments(policyId);
  const generate = useGenerateInstallments();
  const recordPayment = useRecordInstallmentPayment();
  const reinstate = useReinstatePolicy();

  /** null = follow the page holding the next installment due, which is where
   *  the broker almost always wants to be -- and it keeps following after a
   *  payment settles that row. A page the broker picks by hand sticks. */
  const [chosenPage, setChosenPage] = useState<number | null>(null);
  /** B-02: the modal refuses to submit without a mode; this says why. */
  const [payModeError, setPayModeError] = useState('');

  const [payTarget, setPayTarget] = useState<PolicyInstallment | null>(null);
  const [payForm, setPayForm] = useState({
    amount: '',
    paid_on: '',
    payment_mode: '',
    payment_reference: '',
    notes: '',
  });
  const [showReinstate, setShowReinstate] = useState(false);
  const [reinstateNote, setReinstateNote] = useState('');
  /** The server's own refusal sentence, kept on screen. It names the
   *  installments still owed, which is the one thing the broker has to act on;
   *  a generic "could not reinstate" would throw that away. */
  const [reinstateError, setReinstateError] = useState('');

  const openPayment = (installment: PolicyInstallment) => {
    setPayTarget(installment);
    setPayModeError('');
    setPayForm({
      amount: installment.amount,
      paid_on: new Date().toISOString().slice(0, 10),
      payment_mode: '',
      payment_reference: '',
      notes: '',
    });
  };

  const submitPayment = async () => {
    if (!payTarget) return;
    // B-02: every installment payment writes a receipt into Payment History,
    // and the server refuses one without a mode. Say so here rather than let
    // the broker find out from a toast after the click.
    if (!payForm.payment_mode) {
      setPayModeError('Choose how the customer paid');
      return;
    }
    try {
      await recordPayment.mutateAsync({
        policyId,
        data: {
          installment_id: payTarget.id,
          amount: payForm.amount || undefined,
          paid_on: payForm.paid_on || undefined,
          payment_mode: payForm.payment_mode,
          payment_reference: payForm.payment_reference || undefined,
          notes: payForm.notes || undefined,
        },
      });
      setPayTarget(null);
    } catch {
      // The hook has already toasted the server's reason; keep the modal open
      // with what the broker typed so they can correct it.
    }
  };

  const submitReinstate = async () => {
    setReinstateError('');
    try {
      await reinstate.mutateAsync({ policyId, notes: reinstateNote });
      setShowReinstate(false);
      setReinstateNote('');
    } catch (err) {
      setReinstateError(getErrorMessage(err));
    }
  };

  const summary: InstallmentSummary | null = data?.summary ?? null;
  const schedule = data?.schedule ?? [];

  const pageCount = Math.max(1, Math.ceil(schedule.length / INSTALLMENTS_PER_PAGE));
  const nextDueIndex = schedule.findIndex((row) => !row.is_settled && row.status !== 'cancelled');
  const nextDuePage = nextDueIndex < 0 ? pageCount : Math.floor(nextDueIndex / INSTALLMENTS_PER_PAGE) + 1;
  // Clamped: a rebuild that shortens the term can leave a chosen page past the end.
  const page = Math.min(Math.max(chosenPage ?? nextDuePage, 1), pageCount);
  const firstRow = (page - 1) * INSTALLMENTS_PER_PAGE;
  const visibleRows = schedule.slice(firstRow, firstRow + INSTALLMENTS_PER_PAGE);

  return (
    <Card>
      <CardHeader
        title="Premium Paying Term"
        subtitle={`Policy ${policyNumber} — the number stays the same for every installment`}
        action={
          canManage ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => generate.mutate(policyId)}
              isLoading={generate.isPending}
            >
              {schedule.length ? 'Rebuild schedule' : 'Generate schedule'}
            </Button>
          ) : undefined
        }
      />

      {/* 1 of 3 — loading */}
      {isLoading ? (
        <ContentLoader />
      ) : isError ? (
        /* 2 of 3 — the request FAILED. Never rendered as "nothing due". */
        <div className="py-8 text-center">
          <p className="text-sm font-medium text-red-600">
            The installment schedule could not be loaded.
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{getErrorMessage(error)}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => refetch()}
            isLoading={isFetching}
          >
            Try again
          </Button>
        </div>
      ) : schedule.length === 0 ? (
        /* 3 of 3 — the request succeeded and there is genuinely nothing yet. */
        <EmptyState
          title="No installments scheduled yet"
          description="This policy has a payment frequency and a payment count but no schedule. Generate one to start tracking premiums against it."
          action={
            canManage ? (
              <Button onClick={() => generate.mutate(policyId)} isLoading={generate.isPending}>
                Generate schedule
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {summary && (
            <div className="mb-4 grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-4 sm:grid-cols-4 dark:bg-gray-800/60">
              <SummaryCell
                label="Frequency"
                value={`${summary.payment_frequency_display} × ${summary.number_of_payments ?? '—'}`}
              />
              <SummaryCell
                label="Paid"
                value={`${summary.paid_count} of ${summary.total_installments}`}
              />
              <SummaryCell
                label="Next due"
                value={summary.next_due_date ? formatDate(summary.next_due_date) : 'Term complete'}
              />
              <SummaryCell
                label="Term ends"
                value={summary.projected_end_date ? formatDate(summary.projected_end_date) : '—'}
              />
            </div>
          )}

          {summary?.grace_period_days !== null && summary?.grace_period_days !== undefined && (
            <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
              Grace period: {summary.grace_period_days} day(s), set by the{' '}
              {summary.grace_period_source === 'default'
                ? 'platform default'
                : summary.grace_period_source}
              . The policy stays in force until the grace period on an unpaid installment ends —
              not because its end date has passed.
            </p>
          )}

          {summary?.can_reinstate && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-900/20">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                This policy has lapsed.
              </p>
              <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                Record the outstanding installment(s) first — reinstatement does not collect the
                arrears.
              </p>
              {canManage && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => {
                    setReinstateError('');
                    setShowReinstate(true);
                  }}
                >
                  Reinstate policy
                </Button>
              )}
            </div>
          )}

          <div className="-mx-6 overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">#</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Due</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                    Grace until
                  </th>
                  <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                    Amount
                  </th>
                  <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                    Paid
                  </th>
                  <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                    Status
                  </th>
                  <th className="whitespace-nowrap px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                {visibleRows.map((row) => (
                  <tr key={row.id} data-testid={`installment-row-${row.installment_number}`}>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 dark:text-gray-400">{row.installment_number}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                      {formatDate(row.due_date)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 dark:text-gray-400">{formatDate(row.grace_until)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatCurrency(row.amount)}
                    </td>
                    {/* B-02: what was ACTUALLY collected. The Record payment
                        modal lets the amount be edited, and that figure was
                        persisted but never shown -- the row kept displaying
                        the scheduled amount as if that had been paid. */}
                    <td className="whitespace-nowrap px-3 py-2 text-sm">
                      {row.is_settled && row.paid_amount !== null ? (
                        <div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {formatCurrency(row.paid_amount)}
                          </span>
                          {row.paid_at && (
                            <span className="block text-xs text-gray-500 dark:text-gray-400">
                              on {formatDate(row.paid_at)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <Badge
                        variant={
                          row.status === 'paid' || row.status === 'waived'
                            ? 'success'
                            : row.is_past_grace
                              ? 'error'
                              : row.status === 'overdue'
                                ? 'warning'
                                : 'default'
                        }
                        size="sm"
                      >
                        {row.status_display}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {canManage && !row.is_settled && row.status !== 'cancelled' && (
                        <Button variant="outline" size="sm" onClick={() => openPayment(row)}>
                          Record payment
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="-mx-6 mt-2 flex items-center justify-between border-t border-gray-100 px-6 pt-3 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Installments {firstRow + 1}–{firstRow + visibleRows.length} of {schedule.length}
              </p>
              <nav aria-label="Installment pages" className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setChosenPage(page - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Page {page} of {pageCount}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pageCount}
                  onClick={() => setChosenPage(page + 1)}
                >
                  Next
                </Button>
                {page !== nextDuePage && nextDueIndex >= 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setChosenPage(null)}>
                    Next due
                  </Button>
                )}
              </nav>
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={payTarget !== null}
        onClose={() => setPayTarget(null)}
        title={payTarget ? `Record installment #${payTarget.installment_number}` : 'Record payment'}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            This records a premium against policy {policyNumber}. It does not create a new policy
            and does not change the policy number.
          </p>
          <Input
            id="installment-amount"
            label="Amount"
            type="text"
            value={payForm.amount}
            onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))}
          />
          <Input
            id="installment-paid-on"
            label="Payment date"
            type="date"
            value={payForm.paid_on}
            onChange={(e) => setPayForm((f) => ({ ...f, paid_on: e.target.value }))}
          />
          <Select
            id="installment-payment-mode"
            label="Payment mode"
            required
            options={[
              // B-02: no "Not recorded" any more. A mode-less payment settled
              // the row but wrote no receipt, so it was missing from Payment
              // History; the server now requires one.
              { value: '', label: 'Choose a mode' },
              { value: 'online', label: 'Online Payment' },
              { value: 'upi', label: 'UPI' },
              { value: 'bank_transfer', label: 'Bank Transfer' },
              { value: 'cheque', label: 'Cheque' },
              { value: 'demand_draft', label: 'Demand Draft' },
              { value: 'cash', label: 'Cash' },
              { value: 'wallet', label: 'Wallet' },
            ]}
            value={payForm.payment_mode}
            onChange={(e) => {
              setPayModeError('');
              setPayForm((f) => ({ ...f, payment_mode: e.target.value }));
            }}
            error={payModeError || undefined}
            helperText="The payment is also written into Payment History as a receipt."
          />
          <Input
            id="installment-reference"
            label="Reference"
            type="text"
            value={payForm.payment_reference}
            onChange={(e) => setPayForm((f) => ({ ...f, payment_reference: e.target.value }))}
            placeholder="Cheque / UTR / transaction id"
          />
          <Input
            id="installment-notes"
            label="Remark"
            type="text"
            value={payForm.notes}
            onChange={(e) => setPayForm((f) => ({ ...f, notes: e.target.value }))}
          />
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setPayTarget(null)}>
              Cancel
            </Button>
            <Button onClick={submitPayment} isLoading={recordPayment.isPending}>
              Record payment
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showReinstate}
        onClose={() => setShowReinstate(false)}
        title="Reinstate policy"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Reinstatement is a separate, manual step. It brings policy {policyNumber} back into
            force; it does not collect any premium, so every overdue installment must already be
            recorded as paid.
          </p>
          <Input
            id="reinstate-note"
            label="Remark"
            type="text"
            value={reinstateNote}
            onChange={(e) => setReinstateNote(e.target.value)}
            placeholder="Why this policy is being reinstated"
          />
          {reinstateError && (
            <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
              {reinstateError}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowReinstate(false)}>
              Cancel
            </Button>
            <Button onClick={submitReinstate} isLoading={reinstate.isPending}>
              Reinstate
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}

function SummaryCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{value}</p>
    </div>
  );
}


function CommissionBreakdownTables({ breakdown }: { breakdown: CommissionBreakdown }) {
  const posp = breakdown.posp;

  return (
    <div className="mt-6 space-y-6 border-t border-gray-200 dark:border-gray-700 pt-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
          From the insurance company
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {breakdown.components.map((component) => (
                <tr key={component.key}>
                  <td className="py-1.5 pr-4 text-gray-600 dark:text-gray-300">{component.label}</td>
                  <td className="py-1.5 pr-4 text-right whitespace-nowrap text-gray-500 dark:text-gray-400">
                    {/* The rate and the base it applied to. A bare amount is not
                        something anyone can check against an insurer's grid. */}
                    {component.percentage != null && component.premium != null
                      ? `${component.percentage}% of ${formatCurrency(component.premium)}`
                      : component.percentage != null
                        ? `${component.percentage}%`
                        : ''}
                  </td>
                  <td className="py-1.5 text-right whitespace-nowrap font-medium text-gray-900 dark:text-gray-100">
                    {formatCurrency(component.amount)}
                  </td>
                </tr>
              ))}
              {(breakdown.adjustments ?? []).map((adjustment) => (
                <tr key={adjustment.key}>
                  <td className="py-1.5 pr-4 text-gray-600 dark:text-gray-300">{adjustment.label}</td>
                  <td />
                  <td className="py-1.5 text-right whitespace-nowrap font-medium text-gray-900 dark:text-gray-100">
                    {formatCurrency(adjustment.amount)}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-gray-200 dark:border-gray-700">
                <td className="py-1.5 pr-4 font-medium text-gray-900 dark:text-gray-100">Total received</td>
                <td />
                <td className="py-1.5 text-right whitespace-nowrap font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(breakdown.total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {breakdown.partial && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Some commission on this policy was priced by a rate with no OD/TP split, so these
            lines do not add up to the full amount received.
          </p>
        )}
      </div>

      {posp && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
            Paid to the agent
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {posp.components.map((component) => (
                  <tr key={component.key}>
                    <td className="py-1.5 pr-4 text-gray-600 dark:text-gray-300">{component.label}</td>
                    <td className="py-1.5 text-right whitespace-nowrap font-medium text-gray-900 dark:text-gray-100">
                      {formatCurrency(component.amount)}
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-gray-200 dark:border-gray-700">
                  <td className="py-1.5 pr-4 font-medium text-gray-900 dark:text-gray-100">Total paid</td>
                  <td className="py-1.5 text-right whitespace-nowrap font-semibold text-gray-900 dark:text-gray-100">
                    {formatCurrency(posp.total)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/** One titled block of label/value pairs — the premium breakdown and the
 *  vehicle card are both built from these (see policyDetailFields.ts). */
function FieldGroup({ group }: { group: DetailGroup }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
        {group.title}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
        {/* Index in the key: add-on lines are broker-entered names and two
            riders can genuinely share one. */}
        {group.fields.map((field, index) => (
          <InfoItem key={`${field.label}-${index}`} label={field.label} value={field.value} />
        ))}
      </div>
    </div>
  );
}
